import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useAccessToken } from '../../useAccessToken'
import useProjectId from '../../../customHooks/useProjectId'
import { useProjektprognosInteractionContext } from '../../../context/ProjektprognosInteractionContext'
import { formatData } from '../../../../../utils/formatData'
import { makeResultFetch } from '../../genericFetch'
import { GridRow } from '../../../../../interfaces/grids/GridRow'
import { apiRequestParams } from '../../fetchOptions'
import { useHandleRetry } from '../../useHandleRetry'

const useFetch = () => {
  const getAccessToken = useAccessToken()
  const projectId = useProjectId()

  const queryFunction = useCallback(async () => {
    return getAccessToken().then((accessToken) => {
      if (!accessToken) throw new Error('Access token is undefined')

      const fetchedData = makeResultFetch<GridRow[]>(
        `/Sheet/${projectId}/Performance`,
        apiRequestParams(accessToken, 'GET')
      ).then((response) => {
        const { data, message, success } = response

        if (!success) throw Error(message)

        if (data) {
          const formattedData = formatData(data)
          return formattedData
        }
      })

      return fetchedData
    })
  }, [getAccessToken, projectId])

  return { projectId, queryFunction }
}

export const useProjektprognosPerformance = (options?: {
  enabled?: boolean
}) => {
  const { projectId, queryFunction } = useFetch()
  const handleRetry = useHandleRetry()
  const { loadProjektprognos } = useProjektprognosInteractionContext()

  const query = useQuery({
    queryKey: [projectId, 'performance'],
    queryFn: queryFunction,
    enabled: !!projectId && !!queryFunction && (options?.enabled ?? true),
    retry: handleRetry,
  })

  return {
    ...query,
    isLoading: query.isLoading || loadProjektprognos,
  }
}
